import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import urbano from "@/assets/scooter-urbano.webp";
import plegable from "@/assets/scooter-plegable.webp";
import todoterreno from "@/assets/scooter-todoterreno.webp";
import largoAlcance from "@/assets/scooter-largo-alcance.webp";
import ninos from "@/assets/scooter-ninos.webp";
import asiento from "@/assets/scooter-asiento.webp";
import potente from "@/assets/scooter-potente.webp";
import dgt from "@/assets/scooter-dgt.webp";

interface ScooterType {
  label: string;
  q: string;
  img: string;
  badge?: string;
  dgt?: boolean;
}

const TYPES: ScooterType[] = [
  { label: "Urbano", q: "urbano", img: urbano },
  { label: "Plegable", q: "plegable", img: plegable },
  { label: "Todoterreno", q: "todoterreno", img: todoterreno },
  { label: "Largo alcance", q: "largo alcance", img: largoAlcance },
  { label: "Niños", q: "niños", img: ninos },
  { label: "Asiento", q: "asiento", img: asiento },
  { label: "Alta potencia", q: "potente", img: potente, badge: "+1500W" },
  { label: "Homologado DGT", q: "dgt", img: dgt, dgt: true, badge: "DGT" },
];

const TypeCard = ({ type, index, visible }: { type: ScooterType; index: number; visible: boolean }) => {
  return (
    <Link
      to={type.dgt ? "/buscar?dgt=1" : `/buscar?q=${encodeURIComponent(type.q)}`}
      style={{ transitionDelay: visible ? `${index * 70}ms` : "0ms" }}
      className={`group relative flex flex-col items-center justify-between gap-3 overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-4 backdrop-blur-sm transition-all duration-700 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      {/* Línea superior gradiente en hover */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden
      />

      {type.badge && (
        <span className="absolute top-2.5 right-2.5 z-10 rounded-full bg-gradient-to-r from-primary to-accent px-2.5 py-0.5 text-[10px] font-bold text-primary-foreground shadow-md shadow-primary/30">
          {type.badge}
        </span>
      )}

      <div className="relative flex h-32 w-full items-center justify-center overflow-hidden rounded-xl">
        {/* Glow gradiente detrás de la imagen */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-60 transition-opacity duration-300 group-hover:opacity-100"
          aria-hidden
        />
        <div
          className="absolute inset-4 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
          aria-hidden
        />
        <img
          src={type.img}
          alt={`Patinete eléctrico ${type.label}`}
          width={320}
          height={320}
          loading="lazy"
          decoding="async"
          className="relative h-full w-auto object-contain transition-transform duration-500 ease-out group-hover:scale-110 group-hover:-rotate-3"
        />
      </div>

      <div className="flex w-full flex-col items-center gap-1">
        <span className="font-display text-sm font-bold text-foreground">{type.label}</span>
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          Ver modelos <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
};

const ScooterTypes = () => {
  const { ref: headerRef, visible: headerVisible } = useReveal<HTMLDivElement>();
  const { ref: gridRef, visible: gridVisible } = useReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden border-b border-border py-16">
      {/* Glow decorativos */}
      <div className="pointer-events-none absolute -top-20 left-1/4 h-64 w-64 rounded-full bg-primary/10 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-20 right-1/4 h-64 w-64 rounded-full bg-accent/10 blur-3xl" aria-hidden />

      <div className="container relative">
        <div
          ref={headerRef}
          className={`mx-auto max-w-2xl text-center transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Para cada tipo de viaje
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Elige entre la mayor{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              oferta
            </span>
          </h2>
          <p className="mt-3 text-muted-foreground">
            Urbanos, plegables, todoterreno y más. Encuentra el patinete perfecto para ti.
          </p>
        </div>

        <div ref={gridRef} className="mt-10 grid gap-5 grid-cols-2 md:grid-cols-4">
          {TYPES.map((t, idx) => (
            <TypeCard key={t.label} type={t} index={idx} visible={gridVisible} />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Button size="lg" asChild className="gap-2 rounded-full bg-gradient-to-r from-primary to-accent px-8 shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40">
            <Link to="/buscar">
              Ver todos los patinetes <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ScooterTypes;
